import React, { useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import {
  DealerSearchContext,
  DealerSearchContextType,
} from '@rsc/contexts/DealerSearchContext';
import { LocaleContext } from '@rsc/contexts/LocaleContext';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import breakpoints from '../breakpoints';
import LinkComponent from '../LinkComponent';

interface MegaMenuProps {
  megaMenuIndex: number;
  megaMenuActive: number;
  setMegaMenuActive: (index: number) => void;
  keyboardNavActive: boolean;
  menuStructure: any;
  columns: number;
  getInternalLink: (link: any) => string;
}

const Wrapper = styled.menu<{ megaMenuIndex: number; megaMenuActive: number }>`
  box-sizing: border-box;
  border-top: solid 1px inherit;
  border-bottom: solid 1px inherit;
  width: 100%;
  display: ${props =>
    props.megaMenuActive === props.megaMenuIndex ? 'flex' : 'none'};
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  position: absolute;
  padding: 2rem;
  left: 0;
  top: 100px;
  z-index: 2;
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07);

  @media (max-width: calc(${breakpoints.lg} - 1)) {
    display: none;
  }
`;

const MegaMenuInner = styled.ul<{
  columns: number;
  singleLevelLayout: boolean;
}>`
  list-style: none;
  margin: 0 auto;
  padding: 0 1rem;
  box-sizing: border-box;
  width: 100%;
  @media (min-width: ${breakpoints.xl}) {
    width: ${props =>
      props.columns < 5
        ? parseInt(
            ((parseInt(breakpoints.xl, 10) / 5) * props.columns).toString(),
            10
          )
        : parseInt(breakpoints.xl, 10)}px;
  }
  display: ${props => (props.singleLevelLayout ? 'inline-block' : 'grid')};
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  grid-gap: 1rem;
  ${props => props.singleLevelLayout && `column-count: ${props.columns};`}
  color: inherit;

  li {
    margin-bottom: 0.35rem;

    a {
      text-decoration: none;
      color: inherit;
      font-weight: normal;
      &:hover {
        text-decoration: underline;
      }
    }

    h2 {
      font-size: 1.25rem;
      margin-bottom: 0.75rem;
      color: inherit;
      text-transform: ${({ theme }) =>
        theme.navigation.subSectionHeadingTextTransform};
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        a {
          color: inherit;
          font-weight: inherit;
          text-decoration: none;
          font-size: 1rem;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

const MenuFooter = styled.div`
  margin-top: 2.625rem;
`;

const MenuHeading = styled.div`
  color: ${({ theme }) => theme.colors.brandPrimary}!important;
  font-weight: bold !important;
  font-size: 1.25rem;
  display: block;
  margin-bottom: 0.25rem;
  a {
    font-weight: inherit !important;
  }
`;

function MegaMenu({
  megaMenuIndex,
  megaMenuActive = null,
  setMegaMenuActive = () => {},
  keyboardNavActive = false,
  menuStructure,
  columns = 5,
  getInternalLink = () => '',
}: Readonly<MegaMenuProps>) {
  const { tt } = useUiComponentsInternationalContext();
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const additionalLinks = [];
  const lastKeyRef = useRef(null);

  // check if using dealer context
  const dealerSearchContext =
    useContext<DealerSearchContextType>(DealerSearchContext);
  const storedDealer = dealerSearchContext?.storedDealer ?? null;
  const toggleDealerSearch = dealerSearchContext?.toggleDealerSearch ?? null;

  let hasThirdLevel = false;
  menuStructure?.items?.forEach(section => {
    if (!hasThirdLevel && section.items) hasThirdLevel = true;
  });

  if (hasThirdLevel) {
    menuStructure?.items?.forEach(section => {
      if (!section.items) {
        additionalLinks.push(section);
      }
    });
  }

  const handleKeyDown = event => {
    if (event.key === 'Tab' && event.shiftKey) {
      // set last key ref
      lastKeyRef.current = 'shift-tab';
    } else if (event.key === 'Tab') {
      // set last key ref
      lastKeyRef.current = 'tab';
    }
  };

  const firstMenuItems = React.useRef([]);

  useEffect(() => {
    if (megaMenuActive > -1 && keyboardNavActive) {
      const uniqueArray = firstMenuItems.current
        .filter((item, pos) => firstMenuItems.current.indexOf(item) === pos)
        .filter(e => e);

      uniqueArray[0].getElementsByTagName('a')[0].focus();
    }
  }, [keyboardNavActive, megaMenuActive]);

  const menuSectionsWithItems = menuStructure?.items?.filter(
    section => !additionalLinks.some(el => el.title === section.title)
  );

  const handleMegaMenuItemBlur = e => {
    // get current mega menu parent
    const parentMenu = e.target.closest('menu');

    // determine if this item is the first item in the mega menu
    const isFirstChild =
      parentMenu.querySelector('li:first-child a') === e.target;
    if (isFirstChild) {
      if (lastKeyRef.current === 'shift-tab') {
        document.querySelector(`#menu-trigger-${megaMenuIndex}`).focus();
        // unset last key ref
        lastKeyRef.current = null;
        // close mega menu if open
        setMegaMenuActive(null);
      }
    }

    // determine if this item is the last item in the mega menu
    let isLastChild =
      parentMenu.querySelector('li:last-of-type li:last-child a') === e.target;

    if (!isLastChild) {
      const menuFooterLastLink = parentMenu.querySelector(
        '#menu-footer h2:last-child a'
      );

      isLastChild = menuFooterLastLink === e.target;
    }

    if (isLastChild) {
      const nextMegaMenuIndex = document.querySelector(
        `#menu-trigger-${megaMenuIndex + 1}`
      );

      if (lastKeyRef.current === 'tab' && nextMegaMenuIndex) {
        nextMegaMenuIndex.focus();

        // unset last key ref
        lastKeyRef.current = null;
        // close mega menu if open
        setMegaMenuActive(null);
      }
    }
  };

  const getSectionLink = section => {
    if (section?.link?.linkType === 'internal') {
      return (
        <LinkComponent
          to={getInternalLink({
            ...section.link,
            reference: section.link.internalLink,
          })}
          onBlur={handleMegaMenuItemBlur}
          onKeyDown={keyboardNavActive ? handleKeyDown : () => {}}
        >
          {section.title}
        </LinkComponent>
      );
    }

    if (section?.link?.href) {
      return (
        <a
          href={section.link.href}
          target={section?.blank ? `_blank` : `_self`}
          onBlur={handleMegaMenuItemBlur}
          onKeyDown={keyboardNavActive ? handleKeyDown : () => {}}
        >
          {section.title}
        </a>
      );
    }

    return null;
  };

  return (
    <Wrapper
      megaMenuIndex={megaMenuIndex}
      megaMenuActive={megaMenuActive}
      onMouseEnter={() => {
        setMegaMenuActive(megaMenuIndex);
      }}
      onMouseLeave={() => {
        setMegaMenuActive(null);
      }}
      onKeyDown={event => {
        if (keyboardNavActive && event.key === 'Escape') {
          setMegaMenuActive(null);
        }
      }}
      tabIndex={0}
    >
      <MegaMenuInner columns={columns} singleLevelLayout={!hasThirdLevel}>
        {menuSectionsWithItems?.map((section, index) => (
          <li
            key={`${section?.title || section?.items?.[0]?.title}-wrap`}
            ref={el => {
              if (index === 0) {
                firstMenuItems.current.push(el);
              }
            }}
          >
            {/* MegaMenu Item Headings */}
            {section.title && (
              <>
                {/* When Item Heading is a Link, but has no sub-sections,
                    display it as a normal link. */}
                {section.link &&
                  section.link.linkType !== 'disable' &&
                  (!section?.items || !hasThirdLevel) &&
                  getSectionLink(section)}

                {/* When Item Heading is a Link, and has sub-sections,
                    display it as a heading link. */}
                {section.link &&
                  section.link.linkType !== 'disable' &&
                  (section?.items || hasThirdLevel) && (
                    <MenuHeading>{getSectionLink(section)}</MenuHeading>
                  )}

                {/* When Item Heading is not a Link, display it as a heading. */}
                {(!section.link || section?.link?.linkType === 'disable') && (
                  <MenuHeading>{section.title}</MenuHeading>
                )}
              </>
            )}
            {/* MegaMenu Sub-Section Items */}
            {section?.items && (
              <ul>
                {section.items?.map(subSection => (
                  <li
                    key={`${subSection.title}-mega`}
                    className="sub-section-container"
                  >
                    {getSectionLink(subSection)}
                  </li>
                ))}

                {index === 0 && section.title === 'Support' && (
                  <>
                    {storedDealer?.slug && (
                      <LinkComponent
                        to={
                          currency === 'CAD'
                            ? `/en-ca/dealer/${storedDealer.slug}`
                            : `/dealer/${storedDealer.slug}`
                        }
                      >
                        {tt(s => s.headerCommon.yourLocalDealerLink)}
                      </LinkComponent>
                    )}
                    {!storedDealer && dealerSearchContext && (
                      <LinkComponent href="#" onClick={toggleDealerSearch}>
                        {tt(s => s.headerCommon.yourLocalDealerLink)}
                      </LinkComponent>
                    )}
                  </>
                )}

                {/* All Mega Menu items that don't have Sub-Section items,
                    are added to the bottom of last column as MenuFooter. */}
                {index + 1 === menuSectionsWithItems.length &&
                  additionalLinks?.length > 0 && (
                    <MenuFooter id="menu-footer">
                      {additionalLinks.map(additionalSection => (
                        <h2 key={`${additionalSection.title}-heading`}>
                          {getSectionLink(additionalSection)}
                        </h2>
                      ))}
                    </MenuFooter>
                  )}
              </ul>
            )}
          </li>
        ))}
      </MegaMenuInner>
    </Wrapper>
  );
}

export default MegaMenu;
